import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Filter, ArrowUpDown, MoreVertical, MessageSquare, Clock, XCircle, RefreshCw, AlertCircle, Search } from "lucide-react";
import ticketService from "../../services/ticketService";
import websiteService from "../../services/websiteService";
import { formatDistanceToNow } from "date-fns";
import SyncLoadingOverlay from "../../components/SyncLoadingOverlay";
import Toast from "../../components/Toast";
import creditService from "../../services/creditService";
import { useAuth } from "../../context/AuthContext";

const Tickets = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("updated_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // State for API data
  const [tickets, setTickets] = useState([]);
  const [websites, setWebsites] = useState({});
  const [ticketStats, setTicketStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0
  });
  // eslint-disable-next-line no-unused-vars
  const [credits, setCredits] = useState({ support: 0, development: 0 });
  
  // Ref to track if the initial sync was already performed
  const initialSyncPerformed = useRef(false);

  // Map FreeScout status to local status
  const _mapFreescoutStatusToLocal = (freescoutStatus) => {
    // Handle string status values (new format)
    if (typeof freescoutStatus === 'string') {
      const stringStatusMap = {
        'active': 'open',
        'pending': 'pending',
        'closed': 'closed',
        'spam': 'closed',
        'deleted': 'closed'
      };
      
      return stringStatusMap[freescoutStatus.toLowerCase()] || 'open';
    }

    return 'open';
  };

  // Fetch tickets from API
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        limit: pagination.limit,
        offset: pagination.offset
      };
      
      // Only add status if it's not "all"
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      
      const response = await ticketService.getAllTickets(params);
      
      if (response && response.success) {
        setTickets(response.tickets || []);
        setPagination(prev => ({
          ...prev,
          total: response.total || prev.total
        }));
      } else {
        throw new Error(response?.message || "Failed to fetch tickets");
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setToast({
        show: true,
        message: err.message || "Failed to fetch tickets",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.offset, statusFilter]);

  // Fetch ticket statistics
  const fetchTicketStats = useCallback(async () => {
    try {
      const response = await ticketService.getTicketStats();
      
      if (response && response.success) {
        // Transform FreeScout status to our local status using the mapping function
        const transformedStats = {
          ...response.stats,
          by_status: {
            open: 0,
            pending: 0,
            closed: 0
          }
        };
        
        // Process ticket counts according to our status mapping
        Object.entries(response.stats.by_status).forEach(([status, count]) => {
          const mappedStatus = _mapFreescoutStatusToLocal(status);
          transformedStats.by_status[mappedStatus] = (transformedStats.by_status[mappedStatus] || 0) + count;
        });
        
        setTicketStats(transformedStats);
      }
    } catch (err) {
      console.error("Error fetching ticket stats:", err);
    }
  }, []);

  // Fetch website details for all tickets
  const fetchWebsiteDetails = useCallback(async () => {
    try {
      // Get unique website IDs from tickets
      const websiteIds = [...new Set(tickets
        .filter(ticket => ticket.website_id)
        .map(ticket => ticket.website_id))];
      
      if (websiteIds.length === 0) return;
      
      // Fetch each website's details and store in a map
      const websitesMap = {};
      await Promise.all(websiteIds.map(async (websiteId) => {
        try {
          const response = await websiteService.getWebsiteById(websiteId);
          if (response && response.success && response.website) {
            websitesMap[websiteId] = response.website;
          }
        } catch (err) {
          console.error(`Error fetching details for website ${websiteId}:`, err);
        }
      }));
      
      setWebsites(websitesMap);
    } catch (err) {
      console.error("Error fetching website details:", err);
    }
  }, [tickets]);

  // Sync tickets with FreeScout
  const handleSyncTickets = useCallback(async () => {
    try {
      setSyncInProgress(true);
      setToast({
        show: true,
        message: "Syncing tickets from GPM Support...",
        type: "info"
      });
      
      const response = await ticketService.syncTicketsFromFreeScout();
      
      if (response && response.success) {
        // Fetch ticket stats first without showing loading state
        await fetchTicketStats();
        
        // Then fetch tickets without showing loading state
        try {
          const params = {
            limit: pagination.limit,
            offset: pagination.offset
          };
          
          if (statusFilter !== "all") {
            params.status = statusFilter;
          }
          
          const ticketsResponse = await ticketService.getAllTickets(params);
          
          if (ticketsResponse && ticketsResponse.success) {
            setTickets(ticketsResponse.tickets || []);
            setPagination(prev => ({
              ...prev,
              total: ticketsResponse.total || prev.total
            }));
          }
        } catch (err) {
          console.error("Error refreshing tickets:", err);
          // Don't show error toast here as we'll show the success message for the sync
        }
        
        setToast({
          show: true,
          message: response.message || "Tickets synced successfully",
          type: "success"
        });
      } else {
        throw new Error(response?.message || "Failed to sync tickets");
      }
    } catch (err) {
      console.error("Error syncing tickets:", err);
      setToast({
        show: true,
        message: err.message || "Failed to sync tickets",
        type: "error"
      });
    } finally {
      setSyncInProgress(false);
    }
  }, [fetchTicketStats, pagination.limit, pagination.offset, statusFilter]);

  // Update fetchCredits function
  const fetchCredits = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await creditService.getRemainingCredits(user.id);
      if (response && response.success) {
        setCredits({
          support: response.data.support_credits || 0,
          development: response.data.custom_credits || 0
        });
      }
    } catch (error) {
      console.error("Error fetching credits:", error);
    }
  }, [user?.id]);

  // Fetch tickets on component mount and when filters change
  useEffect(() => {
    fetchTickets();
    fetchTicketStats();
    fetchCredits();
  }, [statusFilter, pagination.offset, pagination.limit, fetchTickets, fetchTicketStats, fetchCredits]);
  
  // Fetch website details when tickets change
  useEffect(() => {
    if (tickets.length > 0) {
      fetchWebsiteDetails();
    }
  }, [tickets, fetchWebsiteDetails]);
  
  // Automatically sync tickets with FreeScout when component mounts
  useEffect(() => {
    if (!initialSyncPerformed.current) {
      handleSyncTickets();
      initialSyncPerformed.current = true;
    }
  }, [handleSyncTickets]);

  // Format date to relative time (e.g., "2 days ago")
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (err) {
      console.error("Error formatting date:", err);
      return "Invalid date";
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase() || "";
    
    if (normalizedStatus.includes("open") || normalizedStatus.includes("active")) {
      return "bg-green-100 text-green-800 border border-green-300";
    } else if (normalizedStatus.includes("in progress") || normalizedStatus.includes("pending")) {
      return "bg-amber-100 text-amber-800 border border-amber-300";
    } else if (normalizedStatus.includes("closed") || normalizedStatus.includes("completed")) {
      return "bg-gray-100 text-gray-700 border border-gray-300";
    } else {
      return "bg-gray-100 text-gray-800 border border-gray-300";
    }
  };

  // Get status border color for row
  const getStatusBorderColor = (status) => {
    const normalizedStatus = status?.toLowerCase() || "";
    
    if (normalizedStatus.includes("open") || normalizedStatus.includes("active")) {
      return "border-l-4 border-l-green-500";
    } else if (normalizedStatus.includes("in progress") || normalizedStatus.includes("pending")) {
      return "border-l-4 border-l-amber-500";
    } else if (normalizedStatus.includes("closed") || normalizedStatus.includes("completed")) {
      return "border-l-4 border-l-gray-400";
    } else {
      return "";
    }
  };

  // Get status background color for row
  const getStatusRowStyle = (status) => {
    const normalizedStatus = status?.toLowerCase() || "";
    
    if (normalizedStatus.includes("open") || normalizedStatus.includes("active")) {
      return "bg-green-50 hover:bg-green-100";
    } else if (normalizedStatus.includes("in progress") || normalizedStatus.includes("pending")) {
      return "bg-amber-50 hover:bg-amber-100";
    } else if (normalizedStatus.includes("closed") || normalizedStatus.includes("completed")) {
      return "bg-white hover:bg-gray-50";
    } else {
      return "bg-white hover:bg-gray-50";
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const normalizedStatus = status?.toLowerCase() || "";
    
    if (normalizedStatus.includes("open") || normalizedStatus.includes("active")) {
      return <AlertCircle className="h-3 w-3 mr-1 text-green-600" />;
    } else if (normalizedStatus.includes("in progress") || normalizedStatus.includes("pending")) {
      return <RefreshCw className="h-3 w-3 mr-1 text-amber-600" />;
    } else if (normalizedStatus.includes("closed") || normalizedStatus.includes("completed")) {
      return <XCircle className="h-3 w-3 mr-1 text-gray-500" />;
    } else {
      return null;
    }
  };

  // Get status text color for subject line
  const getStatusSubjectColor = (status) => {
    const normalizedStatus = status?.toLowerCase() || "";
    
    if (normalizedStatus.includes("open") || normalizedStatus.includes("active")) {
      return "text-green-700";
    } else if (normalizedStatus.includes("in progress") || normalizedStatus.includes("pending")) {
      return "text-amber-700";
    } else if (normalizedStatus.includes("closed") || normalizedStatus.includes("completed")) {
      return "text-blue-600"; // Keep default link color for closed
    } else {
      return "text-blue-600";
    }
  };

  // Handle navigation to ticket details page
  const handleRowClick = (id, event) => {
    // Prevent navigation if clicking on dropdown toggle button or dropdown menu
    if (
      event.target.closest('button') || 
      event.target.closest('.dropdown-menu')
    ) {
      return;
    }
    
    // Navigate to ticket details
    window.location.href = `/dashboard/tickets/${id}`;
  };

  // Apply filters and sorting
  const filteredTickets = tickets
    .map(ticket => {
      // Parse freescout_data if it's a string
      if (ticket.freescout_data && typeof ticket.freescout_data === 'string') {
        try {
          ticket = {
            ...ticket,
            freescout_data: JSON.parse(ticket.freescout_data)
          };
        } catch (error) {
          console.error("Error parsing freescout_data:", error);
          // Keep the original ticket data if parsing fails
        }
      }
      return ticket;
    })
    .filter((ticket) => {
      // Apply search filter
      if (searchQuery && !ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by selected field
      let aValue, bValue;
      
      // Special case for updated_at to use freescout_data.updatedAt
      if (sortField === "updated_at") {
        aValue = a.freescout_data?.updatedAt || a.updated_at;
        bValue = b.freescout_data?.updatedAt || b.updated_at;
      } else if (sortField === "created_at") {
        aValue = a.freescout_data?.createdAt || a.created_at;
        bValue = b.freescout_data?.createdAt || b.created_at;
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }
      
      // Handle date comparisons
      if (sortField === "created_at" || sortField === "updated_at") {
        if (sortDirection === "asc") {
          return new Date(aValue) - new Date(bValue);
        } else {
          return new Date(bValue) - new Date(aValue);
        }
      } 
      // Handle string comparisons
      else if (typeof aValue === "string" && typeof bValue === "string") {
        if (sortDirection === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } 
      // Handle other comparisons
      else {
        if (sortDirection === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
    });

  // Toggle sort direction
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Toggle dropdown menu
  const toggleDropdown = (id) => {
    if (activeDropdown === id) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(id);
    }
  };

  // Handle pagination
  const handleNextPage = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination(prev => ({
        ...prev,
        offset: prev.offset + prev.limit
      }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit)
      }));
    }
  };

  return (
    <div className="container p-4 mx-auto">
      {/* Toast notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
      
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Support Tickets</h1>
          <p className="text-gray-500">Manage your support requests</p>
          
          {/* Ticket statistics */}
          {ticketStats && (
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                <AlertCircle className="h-3 w-3 mr-1" />
                Open: {ticketStats.by_status.open || 0}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                <RefreshCw className="h-3 w-3 mr-1" />
                In Progress: {ticketStats.by_status.pending || 0}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                <XCircle className="h-3 w-3 mr-1" />
                Closed: {ticketStats.by_status.closed || 0}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
                Total: {ticketStats.total || 0}
              </span>
            </div>
          )}
        </div>
        <div className="mt-4 md:mt-0 flex flex-wrap gap-3">
          <button
            onClick={handleSyncTickets}
            disabled={syncInProgress}
            className="inline-flex items-center gap-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {syncInProgress ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Sync Tickets
          </button>
          
          <Link 
            to="/dashboard/tickets/new"
            className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <PlusCircle className="h-4 w-4" />
            New Support Ticket
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div className="flex-none md:w-48">
            <label htmlFor="status-filter" className="sr-only">Status</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Filter className="h-4 w-4 text-gray-400" />
              </div>
              <select
                id="status-filter"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tickets Table with Loading State */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">
        {loading && <SyncLoadingOverlay />}
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3">
                  <button 
                    className="flex items-center"
                    onClick={() => toggleSort("subject")}
                  >
                    Subject
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3">
                  <button 
                    className="flex items-center"
                    onClick={() => toggleSort("website_id")}
                  >
                    Website
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3">
                  <button 
                    className="flex items-center"
                    onClick={() => toggleSort("status")}
                  >
                    Status
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3">
                  <button 
                    className="flex items-center"
                    onClick={() => toggleSort("created_at")}
                  >
                    Created
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3">
                  <button 
                    className="flex items-center"
                    onClick={() => toggleSort("updated_at")}
                  >
                    Last Updated
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3">
                  <span className="flex items-center">
                    Credit Cost
                    <span className="ml-1 text-xs text-gray-500">(Support/Custom)</span>
                  </span>
                </th>
                <th scope="col" className="px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map((ticket) => (
                <tr 
                  key={ticket.id} 
                  className={`border-b ${getStatusBorderColor(ticket.status)} ${getStatusRowStyle(ticket.status)} cursor-pointer`}
                  onClick={(event) => handleRowClick(ticket.id, event)}
                >
                  <td className="px-6 py-4 font-medium">
                    <div className={`${getStatusSubjectColor(ticket.status)}`}>
                      {ticket.subject}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center">
                      <MessageSquare className="h-3 w-3 mr-1" />
                      {ticket.freescout_data && ticket.freescout_data.threadsCount !== undefined ? ticket.freescout_data.threadsCount : 0} messages
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {ticket.website_id && websites[ticket.website_id] ? (
                      <a 
                        href={`/dashboard/websites/${ticket.website_id}`} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:underline hover:text-blue-800"
                        onClick={(e) => e.stopPropagation()} // Prevent row click event
                      >
                        {websites[ticket.website_id].root_url}
                      </a>
                    ) : (
                      <span>{ticket.website?.name || "via Email"}</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {getStatusIcon(ticket.status)}
                      {ticket.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{formatRelativeTime(ticket.freescout_data?.createdAt)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{formatRelativeTime(ticket.freescout_data?.updatedAt)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    {ticket.support_credits || ticket.custom_credits ? (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${ticket.support_credits ? 'text-blue-600' : 'text-gray-400'}`}>
                            {ticket.support_credits || '-'} support
                          </span>
                          <span className="text-gray-300">/</span>
                          <span className={`text-sm ${ticket.custom_credits ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {ticket.custom_credits || '-'} custom
                          </span>
                        </div>
                        {ticket.credit_confirmed ? (
                          <span className="text-xs text-green-600 mt-1">✓ Confirmed</span>
                        ) : ticket.support_credits || ticket.custom_credits ? (
                          <span className="text-xs text-amber-600 mt-1">Pending confirmation</span>
                        ) : (
                          <span className="text-xs text-gray-500 mt-1">Under review</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Under review</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right relative">
                    <div className="flex items-center justify-end">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        ticket.status?.toLowerCase().includes("open") || ticket.status?.toLowerCase().includes("active") ? "bg-green-500" :
                        ticket.status?.toLowerCase().includes("in progress") || ticket.status?.toLowerCase().includes("pending") ? "bg-amber-500" :
                        "bg-gray-400"
                      }`}></span>
                      <button
                        onClick={() => toggleDropdown(ticket.id)}
                        className="font-medium text-gray-500 hover:text-gray-900"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                    </div>
                    
                    {/* Dropdown menu */}
                    {activeDropdown === ticket.id && (
                      <div className="dropdown-menu fixed right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200" style={{ transform: 'translateX(-3rem)' }}>
                        <div className="py-1">
                          <Link 
                            to={`/dashboard/tickets/${ticket.id}`}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            View Details
                          </Link>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              
              {!loading && filteredTickets.length === 0 && (
                <tr className="bg-white border-b">
                  <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                    No tickets found. Try adjusting your filters or create a new support ticket.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {!loading && pagination.total > 0 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing {Math.min(pagination.offset + 1, pagination.total)} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} tickets
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={pagination.offset === 0}
                className="px-3 py-1 bg-gray-100 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={pagination.offset + pagination.limit >= pagination.total}
                className="px-3 py-1 bg-gray-100 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Support Info */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4 text-blue-800">Get Help With Your Website</h2>
        <p className="text-blue-700 mb-4">
          Create a diagnostic ticket for free and let us help you identify your website concerns. Our team will analyze your issue and provide a precise credit cost estimate before any work begins.
        </p>
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="bg-white p-3 rounded-md border border-blue-200 flex-1 min-w-[250px]">
            <h3 className="font-medium text-blue-800 mb-1">Free Diagnosis</h3>
            <p className="text-sm text-gray-600">Submit a ticket to get a free assessment of your website issues with no obligation.</p>
          </div>
          <div className="bg-white p-3 rounded-md border border-blue-200 flex-1 min-w-[250px]">
            <h3 className="font-medium text-blue-800 mb-1">Credit Cost Calculation</h3>
            <p className="text-sm text-gray-600">Receive a transparent estimate of required credits before approving any work.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link 
            to="/dashboard/tickets/new" 
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Submit New Ticket
          </Link>
          <a 
            href="mailto:support@gravitypointmedia.com" 
            className="inline-flex items-center justify-center px-4 py-2 border border-blue-300 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
          >
            Email Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default Tickets;